// C7 — shared helpers for the admin register CSV exports (AttendeesPage,
// EventRegisterPage, AttendanceReportContent). One row per attendee, with
// per-attendee Photo Permission and Dietary. Kept structural (not tied to any
// one page's local RegistrationAttendee interface) so all three can reuse them.

export interface AttendeeLike {
  familyMember?: {
    name: string;
    relationship?: string;
    photoPermission?: boolean;
    dietaryRequirements?: string | null;
  } | null;
  eventGuest?: {
    firstName: string;
    lastName: string;
    relationship?: string;
    dietaryRequirements?: string | null;
  } | null;
}

/**
 * CSV-quote a cell: wrap in double quotes and double any internal quote. Also
 * neutralises spreadsheet formula injection — dietary is free text a family
 * types, and these registers are opened in Excel; a cell starting with
 * = + - @ (or a control char) is prefixed with a single quote so Excel treats
 * it as text, not a formula.
 */
export function csvEsc(value: string | number): string {
  let s = String(value);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

/** Display name for an attendee (family member XOR guest). */
export function attendeeName(a: AttendeeLike): string {
  if (a.familyMember) return a.familyMember.name;
  if (a.eventGuest) return `${a.eventGuest.firstName} ${a.eventGuest.lastName}`.trim();
  return 'Unknown';
}

/**
 * Photo permission label: 'Yes' / 'No' for family members (from CRM consent),
 * 'N/A' for guests (no consent record — never auto-photographable).
 */
export function photoPermLabel(a: AttendeeLike): string {
  if (a.familyMember) return a.familyMember.photoPermission ? 'Yes' : 'No';
  return 'N/A';
}

/**
 * Phase 0 — fail-closed ON-SCREEN photo-permission label for the register /
 * check-in screen. "Photos OK" is shown ONLY when consent is positively
 * confirmed granted (`=== true`); every other value (false / undefined / null,
 * incl. a not-yet-synced or errored refresh) reads as "not confirmed" so a
 * child is never displayed as photographable on a stale positive.
 */
export function photoStatusLabel(photoPermission: boolean | undefined | null): string {
  return photoPermission === true ? 'Photos OK' : 'No photos — not confirmed';
}

/** Dietary note for an attendee (member or guest); '' when unset. */
export function dietaryLabel(a: AttendeeLike): string {
  return (a.familyMember?.dietaryRequirements ?? a.eventGuest?.dietaryRequirements ?? '').trim();
}
